import type React from "react";
import {DescriptionOutlined, GavelOutlined, PaidOutlined, WarningAmberOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2DiscountOffer} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {discountApr} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatEur, formatInt} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2DiscountsQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2AprCalculator} from "~/experiments/pages/payment-terms-v2/components/PtV2AprCalculator.tsx";
import {usePtV2Period} from "~/experiments/pages/payment-terms-v2/usePtV2Period.ts";

/** The workpaper's APR when it has one, else computed from the discount terms; as a 0–1 ratio. */
function offerApr(o: PtV2DiscountOffer): number | null {
    if (o.aprPct != null) return o.aprPct / 100;
    return o.discountPct != null && o.discountDays != null && o.netDays != null
        ? discountApr(o.discountPct, o.discountDays, o.netDays)
        : null;
}

function offerStatus(o: PtV2DiscountOffer): React.ReactNode {
    if (o.isPenalty) return <Tag color="error" label="Surcharge"/>;
    const apr = offerApr(o);
    if (apr != null) return <Tag color="success" label={`${(apr * 100).toFixed(1)}% APR`}/>;
    return o.valueText ? <b className="ptv2-num">{o.valueText}</b> : null;
}

const OfferList: React.FC<{offers: PtV2DiscountOffer[]; empty: string}> = ({offers, empty}) => (
    offers.length === 0
        ? <p className="ptv2-muted">{empty}</p>
        : <div className="ptv2-rows">
            {offers.map((o, i) => <div className={'ptv2-row' + (o.isPenalty ? ' is-penalty' : '')} key={`${o.supplier}-${i}`}>
                <div className="ptv2-row-main">
                    <b>{o.supplier}</b>{o.category && <span className="ptv2-faint"> · {o.category}</span>}
                    <div className="ptv2-quote">“{o.rawText}”</div>
                    {o.recommendedAction && <div className="ptv2-muted">{o.recommendedAction}</div>}
                </div>
                <div className="ptv2-offer-side">
                    {offerStatus(o)}
                    {o.plPotential != null && <span className="ptv2-good ptv2-num" title="Annual P&L potential">
                        {formatEur(o.plPotential)} / yr
                    </span>}
                </div>
            </div>)}
        </div>
);

export const DiscountsTab: React.FC = () => {
    const {summary, hasData, costOfCapitalPct} = usePtV2Context();
    const discountsQ = usePtV2DiscountsQuery(hasData);
    const offers = discountsQ.data ?? [];
    const invoice = offers.filter(o => o.source === 'invoice');
    const contract = offers.filter(o => o.source === 'contract');
    const penalties = offers.filter(o => o.isPenalty).length;
    const k = summary?.kpis;
    const period = usePtV2Period('discounts');

    const kpis: SitKpiPill[] = [
        {tone: 'ok', icon: <PaidOutlined/>, label: 'Invoice discount terms', value: formatInt(k?.invoiceDiscountOffers)},
        {tone: 'plain', icon: <GavelOutlined/>, label: 'Contract discounts', value: formatInt(k?.contractDiscountOffers)},
        {tone: 'info', icon: <DescriptionOutlined/>, label: 'Invoices with discount amount', value: formatInt(k?.invoicesWithDiscount)},
        {tone: 'warn', icon: <WarningAmberOutlined/>, label: 'Late-payment surcharges', value: formatInt(penalties)},
    ];

    return <PtV2TabFrame
        tab="discounts"
        kpis={kpis}
        kpiAside={period}
        loading={discountsQ.isPending}
        error={discountsQ.isError}
        summary={<>Cash discounts in contracts and on invoice headers that are paid on standard terms today.{' '}
            <b>{formatInt(k?.invoicesWithDiscount)}</b> PDF invoices carry a discount amount.</>}>
        <div className="ptv2-section">
            {/* keyed on the rate, so the slider starts at it once the settings load */}
            <PtV2AprCalculator key={costOfCapitalPct ?? 'none'} costOfCapitalPct={costOfCapitalPct}/>
            <div className="ptv2-grid2">
                <PtV2Card title="Invoices offering a cash discount">
                    <OfferList offers={invoice} empty="No invoice discount terms in this data."/>
                </PtV2Card>
                <PtV2Card title="Discounts written into contracts">
                    <OfferList offers={contract} empty="No contract discounts in this data."/>
                </PtV2Card>
            </div>
        </div>
    </PtV2TabFrame>;
};
